/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: CSM.SYSTEM.AI_CONFIG
 *
 * Admin console for the Contextual AI Assistance sub-module:
 *   • Global status (reads the CSM.AI.ENABLED system parameter)
 *   • Per-screen opt-in grid (enable, Smart Query, Smart Paste, redacted PII
 *     fields, max context rows) backed by csm_ai_screen_config
 *   • Read-only viewer of recent csm_ai_interaction_log activity
 *
 * Access is admin-only (mirrors the RLS policies in migration 36). All writes
 * stamp modified_by via getForensicActorId() so mimic sessions attribute the
 * real admin.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { useLookupNames } from '../../lib/use_lookup_names';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'CSM.SYSTEM.AI_CONFIG';

const LOG_STATUS_META: Record<string, { labelKey: string; label: string; variant: StatusVariant }> = {
  OK:       { labelKey: 'ai.config.status.ok',       label: 'OK',       variant: 'success' },
  REJECTED: { labelKey: 'ai.config.status.rejected', label: 'Rejected', variant: 'warning' },
  ERROR:    { labelKey: 'ai.config.status.error',    label: 'Error',    variant: 'danger' },
};

interface ScreenConfigRow {
  ai_screen_config_id: number;
  page_internal_code:  string;
  is_enabled:          'Y' | 'N';
  allow_nlq:           'Y' | 'N';
  allow_smart_paste:   'Y' | 'N';
  redacted_fields:     string[];
  max_context_rows:    number;
}

interface LogRow {
  ai_interaction_log_id: number;
  log_timestamp:         string;
  user_id:               number | null;
  page_code:             string | null;
  task_type:             string;
  provider:              string | null;
  model:                 string | null;
  tokens_prompt:         number | null;
  tokens_completion:     number | null;
  redacted_field_count:  number;
  status:                string;
  error_code:            string | null;
  latency_ms:            number | null;
}

const LOG_LIMIT = 100;

export interface AiConfigPageProps { session: AuthSession; }

export function AiConfigPage({ session }: AiConfigPageProps) {
  return (
    <PersonalizationProvider pageCode="CSM.SYSTEM.AI_CONFIG" session={session}>
      <AiConfigPageInner session={session} />
    </PersonalizationProvider>
  );
}

function AiConfigPageInner({ session }: AiConfigPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const pageNames = useLookupNames('csm_page_master', 'csm_page_master_tl');
  const [globalOn, setGlobalOn] = useState<boolean | null>(null);
  const [configs, setConfigs]   = useState<ScreenConfigRow[]>([]);
  const [logs, setLogs]         = useState<LogRow[]>([]);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [newCode, setNewCode]   = useState('');
  const detail = useExpandedRows<number>();

  // §17.B13 related children (expand/contract) — the recent AI interactions logged
  // against this screen, labelled by localized status (never a raw id/code; the
  // timestamp and task type ride along in `title`).
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => {
    const row = configs.find(c => c.ai_screen_config_id === id);
    const code = row?.page_internal_code;
    return [
      {
        key: 'activity',
        table: 'csm_ai_interaction_log', pkColumn: 'ai_interaction_log_id',
        title: t('ai.config.related.activity', 'Recent Activity'),
        items: (code ? logs.filter(l => l.page_code === code) : []).map(l => ({
          id: l.ai_interaction_log_id,
          label: LOG_STATUS_META[l.status] ? t(LOG_STATUS_META[l.status].labelKey, LOG_STATUS_META[l.status].label) : l.status,
          title: `${new Date(l.log_timestamp).toLocaleString()} · ${l.task_type}`,
        })),
      },
    ];
  };
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const { data: gp } = await apiDb
        .from('csm_system_parameters')
        .select('parameter_value')
        .eq('internal_code', 'CSM.AI.ENABLED')
        .maybeSingle();
      setGlobalOn((gp as { parameter_value?: string } | null)?.parameter_value === 'Y');

      const { data: cfg, error: cErr } = await apiDb
        .from('csm_ai_screen_config')
        .select('ai_screen_config_id, page_internal_code, is_enabled, allow_nlq, allow_smart_paste, redacted_fields, max_context_rows')
        .order('page_internal_code', { ascending: true });
      if (cErr) throw cErr;
      setConfigs((cfg ?? []) as ScreenConfigRow[]);

      const { data: log, error: lErr } = await apiDb
        .from('csm_ai_interaction_log')
        .select('ai_interaction_log_id, log_timestamp, user_id, page_code, task_type, provider, model, tokens_prompt, tokens_completion, redacted_field_count, status, error_code, latency_ms')
        .order('log_timestamp', { ascending: false })
        .limit(LOG_LIMIT);
      if (lErr) throw lErr;
      setLogs((log ?? []) as LogRow[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const patch = async (row: ScreenConfigRow, values: Partial<ScreenConfigRow>) => {
    setError(null);
    const { error: e } = await routedUpdate(
      session, 'csm_ai_screen_config', 'ai_screen_config_id', row.ai_screen_config_id, values,
    );
    if (e) { setError(e.message); return; }
    setConfigs(prev => prev.map(c =>
      c.ai_screen_config_id === row.ai_screen_config_id ? { ...c, ...values } : c));
  };

  const toggle = (row: ScreenConfigRow, field: 'is_enabled' | 'allow_nlq' | 'allow_smart_paste') =>
    patch(row, { [field]: row[field] === 'Y' ? 'N' : 'Y' } as Partial<ScreenConfigRow>);

  const addScreen = async () => {
    const code = newCode.trim().toUpperCase();
    if (!code) return;
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedInsert(session, 'csm_ai_screen_config', {
        page_internal_code: code,
        is_enabled: 'N', allow_nlq: 'Y', allow_smart_paste: 'Y',
        redacted_fields: [], max_context_rows: 50,
      });
      if (e) throw e;
      setNewCode('');
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const statusBadge = (s: string) => (
    <StatusBadge
      label={LOG_STATUS_META[s] ? t(LOG_STATUS_META[s].labelKey, LOG_STATUS_META[s].label) : s}
      variant={LOG_STATUS_META[s]?.variant ?? 'danger'}
    />
  );

  const tokenTotal = useMemo(
    () => logs.reduce((sum, l) => sum + (l.tokens_prompt ?? 0) + (l.tokens_completion ?? 0), 0),
    [logs],
  );

  const CONFIG_GRID_COLS = useMemo<GridColumnDef[]>(() => [
    { key: 'page_internal_code', header: t('ai.config.col_page'),     defaultWidth: 240 },
    { key: 'is_enabled',         header: t('ai.config.col_enabled'),  defaultWidth: 100 },
    { key: 'allow_nlq',          header: t('ai.config.col_nlq'),      defaultWidth: 100 },
    { key: 'allow_smart_paste',  header: t('ai.config.col_paste'),    defaultWidth: 100 },
    { key: 'redacted_fields',    header: t('ai.config.col_redacted'), defaultWidth: 240 },
    { key: 'max_context_rows',   header: t('ai.config.col_rows'),     defaultWidth: 100 },
  ], [t]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between border-b border-csm-border bg-white px-4 py-2">
        <div>
          <h1 className="text-csm-md font-semibold text-csm-ink">{t('ai.config.title')}</h1>
          <p className="text-csm-xs text-csm-muted">{t('ai.config.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void reload()}
            className="h-7 rounded border border-csm-border px-3 text-csm-sm text-csm-ink-soft hover:bg-csm-surface-alt"
          >
            {t('common.refresh')}
          </button>
          <PersonalizationToggle />
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {/* Global status banner */}
        {globalOn !== null && (
          <div className={
            'mb-3 rounded border px-3 py-2 text-csm-sm ' +
            (globalOn
              ? 'border-csm-success-border bg-csm-success-soft text-csm-success'
              : 'border-csm-warning-border bg-csm-warning-soft text-csm-warning')
          }>
            {globalOn ? t('ai.config.global_on') : t('ai.config.global_off')}
          </div>
        )}

        {error && (
          <ErrorBanner
            message={t('ai.config.load_failed', "Couldn't load AI configuration.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        {/* Per-screen config */}
        <div className="mb-2 flex items-center gap-2">
          <input
            type="text"
            value={newCode}
            onChange={e => setNewCode(e.target.value)}
            placeholder={t('ai.config.page_code_placeholder')}
            className="h-7 w-72 rounded border border-csm-border px-2 text-csm-sm focus:border-primary focus:outline-none"
          />
          <button
            type="button"
            onClick={() => void addScreen()}
            disabled={busy || !newCode.trim()}
            className="h-7 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60"
          >
            + {t('ai.config.add_screen')}
          </button>
        </div>

        <div className="mb-6">
          <PersonalizedGrid componentKey="grid" columns={CONFIG_GRID_COLS}>
            {(visibleCols, widths) => (
              <table className="w-full min-w-[760px] w-max border-collapse text-csm-sm">
                <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-muted">
                  <tr>
                    <th className="w-7 border-b border-csm-border px-3 py-1.5 text-left" title={t('ai.config.related.header', 'Related records')} />
                    {visibleCols.map(col => (
                      <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {configs.map(row => (
                    <Fragment key={row.ai_screen_config_id}>
                    <tr className="hover:bg-csm-surface-alt">
                      <td className="border-b border-csm-border-soft px-3 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                        <RelatedExpandButton open={detail.isOpen(row.ai_screen_config_id)} hasChildren={hasRelated(row.ai_screen_config_id)} onToggle={() => detail.toggle(row.ai_screen_config_id)} expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'page_internal_code' && <span className="text-csm-ink">{pageNames[row.page_internal_code] ?? row.page_internal_code}</span>}
                          {col.key === 'is_enabled' && (
                            canChange && (<button type="button" onClick={() => void toggle(row, 'is_enabled')} title={t('ai.config.toggle_tooltip', 'Toggle')}>
                              <YesNoBadge value={row.is_enabled} yesLabel={t('common.yes')} noLabel={t('common.no')} />
                            </button>)
                          )}
                          {col.key === 'allow_nlq' && (
                            canChange && (<button type="button" onClick={() => void toggle(row, 'allow_nlq')} title={t('ai.config.toggle_tooltip', 'Toggle')}>
                              <YesNoBadge value={row.allow_nlq} yesLabel={t('common.yes')} noLabel={t('common.no')} />
                            </button>)
                          )}
                          {col.key === 'allow_smart_paste' && (
                            canChange && (<button type="button" onClick={() => void toggle(row, 'allow_smart_paste')} title={t('ai.config.toggle_tooltip', 'Toggle')}>
                              <YesNoBadge value={row.allow_smart_paste} yesLabel={t('common.yes')} noLabel={t('common.no')} />
                            </button>)
                          )}
                          {col.key === 'redacted_fields' && (
                            <input
                              type="text"
                              defaultValue={(row.redacted_fields ?? []).join(', ')}
                              placeholder={t('ai.config.redacted_placeholder')}
                              onBlur={e => {
                                const fields = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                                void patch(row, { redacted_fields: fields });
                              }}
                              className="h-7 w-56 rounded border border-csm-border px-2 text-csm-xs focus:border-primary focus:outline-none"
                            />
                          )}
                          {col.key === 'max_context_rows' && (
                            <input
                              type="number" min={0} max={500}
                              defaultValue={row.max_context_rows}
                              onBlur={e => {
                                const n = parseInt(e.target.value, 10);
                                if (!isNaN(n)) void patch(row, { max_context_rows: Math.max(0, Math.min(500, n)) });
                              }}
                              className="h-7 w-16 rounded border border-csm-border px-1 text-csm-xs focus:border-primary focus:outline-none"
                            />
                          )}
                        </td>
                      ))}
                    </tr>
                    {detail.isOpen(row.ai_screen_config_id) && (
                      <tr><td colSpan={visibleCols.length + 1} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildRelatedGroups(row.ai_screen_config_id)} session={session} /></td></tr>
                    )}
                    </Fragment>
                  ))}
                  {configs.length === 0 && !busy && (
                    <tr><td colSpan={visibleCols.length + 1} className="px-3 py-4">
                      <EmptyState
                        variant="no-data"
                        title={t('ai.config.no_config')}
                        action={
                          <button
                            type="button"
                            onClick={() => void addScreen()}
                            disabled={busy || !newCode.trim()}
                            className="h-7 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60"
                          >
                            + {t('ai.config.add_screen')}
                          </button>
                        }
                      />
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
          </PersonalizedGrid>
        </div>

        {/* Recent activity */}
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">{t('ai.config.recent_calls')}</h2>
          <span className="text-csm-xs text-csm-muted">{t('ai.config.token_total', '{{total}} tokens', { total: tokenTotal.toLocaleString() })}</span>
        </div>
        <table className="w-full min-w-[760px] border-collapse text-csm-sm">
          <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-muted">
            <tr>
              <th className="border-b border-csm-border px-3 py-1.5 text-left">{t('ai.config.col_when')}</th>
              <th className="border-b border-csm-border px-3 py-1.5 text-left">{t('ai.config.col_page')}</th>
              <th className="border-b border-csm-border px-3 py-1.5 text-left">{t('ai.config.col_task')}</th>
              <th className="border-b border-csm-border px-3 py-1.5 text-left">{t('ai.config.col_model', 'Model')}</th>
              <th className="csm-num border-b border-csm-border px-3 py-1.5 text-right">{t('ai.config.col_tokens')}</th>
              <th className="csm-num border-b border-csm-border px-3 py-1.5 text-right">{t('ai.config.col_redacted')}</th>
              <th className="border-b border-csm-border px-3 py-1.5">{t('ai.config.col_status')}</th>
            </tr>
          </thead>
          <tbody>
            {logs.map(l => (
              <tr key={l.ai_interaction_log_id} className="hover:bg-csm-surface-alt">
                <td className="border-b border-csm-border-soft px-3 py-1.5 font-mono text-csm-xs text-csm-ink-soft whitespace-nowrap">
                  {new Date(l.log_timestamp).toLocaleString()}
                </td>
                <td className="border-b border-csm-border-soft px-3 py-1.5 text-csm-xs" title={l.page_code ?? ''}>{l.page_code == null ? '—' : (pageNames[l.page_code] ?? l.page_code)}</td>
                <td className="border-b border-csm-border-soft px-3 py-1.5">{l.task_type}</td>
                <td className="border-b border-csm-border-soft px-3 py-1.5 text-csm-xs text-csm-muted">{l.model ?? '—'}</td>
                <td className="csm-num border-b border-csm-border-soft px-3 py-1.5 text-right font-mono text-csm-xs">
                  {((l.tokens_prompt ?? 0) + (l.tokens_completion ?? 0)) || '—'}
                </td>
                <td className="csm-num border-b border-csm-border-soft px-3 py-1.5 text-right font-mono text-csm-xs">{l.redacted_field_count}</td>
                <td className="border-b border-csm-border-soft px-3 py-1.5 text-center">
                  {statusBadge(l.status)}
                  {l.error_code && <div className="mt-0.5 text-csm-2xs text-csm-danger">{l.error_code}</div>}
                </td>
              </tr>
            ))}
            {logs.length === 0 && !busy && (
              <tr><td colSpan={7} className="px-3 py-4"><EmptyState variant="no-data" title={t('ai.config.no_activity')} /></td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
